import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import { useNotifications } from '../NotificationSystem';
import { APP_LOGO_SRC } from '../../constants/appLogo';
import './PasswordSetup.css';

const PasswordSetup = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    setToken(tokenFromUrl);

    if (tokenFromUrl) {
      // Validate token and get user info
      validateToken(tokenFromUrl);
    }
  }, []);

  const validateToken = async (userToken) => {
    try {
      setLoading(true);
      // You could add a Cloud Function to validate the token and return user info
      // For now, we'll assume the token is valid
      setUserInfo({ uid: userToken });
    } catch (error) {
      console.error('Token validation failed:', error);
      setError('Invalid or expired setup link. Please contact your administrator.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSetup = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!token) {
      setError('Invalid setup link');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Setting up password for user:', token);
      
      // Call Cloud Function to set up password
      const result = await authService.setupPassword(token, password);
      
      console.log('Password setup successful:', result);
      
      if (onSuccess) {
        onSuccess(result.userData);
      }
      
    } catch (error) {
      console.error('Password setup failed:', error);
      
      let errorMessage = 'Failed to set up password. Please try again.';
      
      if (error.message.includes('invalid-token')) {
        errorMessage = 'Invalid or expired setup link. Please contact your administrator.';
      } else if (error.message.includes('weak-password')) {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !userInfo) {
    return (
      <div className="password-setup-page">
        <div className="password-setup-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Validating setup link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userInfo) {
    return (
      <div className="password-setup-page">
        <div className="password-setup-container">
          <div className="error-state">
            <div className="error-icon">❌</div>
            <h2>Setup Link Invalid</h2>
            <p>{error}</p>
            <button 
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-setup-page">
      <div className="password-setup-container">
        {/* Logo */}
        <div className="logo-section">
          <img 
            src={APP_LOGO_SRC} 
            alt="Hospital Logo" 
            className="setup-logo"
          />
        </div>

        {/* Title */}
        <div className="title-section">
          <h1>Complete Your Account Setup</h1>
          <p>Set up your password to access the ZeroPath system</p>
        </div>

        {error && (
          <div className="setup-error">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Password Setup Form */}
        <form onSubmit={handlePasswordSetup} className="password-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your new password"
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          <button 
            type="submit" 
            className="setup-button"
            disabled={loading}
          >
            {loading ? 'Setting up...' : 'Complete Setup'}
          </button>
        </form>

        {/* Help Text */}
        <div className="help-section">
          <p className="help-text">
            <strong>Password Requirements:</strong><br/>
            • At least 6 characters long<br/>
            • Use a combination of letters, numbers, and symbols for better security
          </p>
          <p className="support-text">
            Need help? Contact IT Support
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordSetup;
